// @flow

import {Heading} from 'docs/components';
import * as React from 'react';
import {useState} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import Badge from 'sail/Badge';
import Box from 'sail/Box';
import Group from 'sail/Group';
import Icon from 'sail/Icon';
import {Text} from 'sail/Text';
import {ExpandableDetailsButton} from './ExpandableDetailsButton';

export type RoleDescriptionProps = {|
  roleName: React.Node,
  isBeta: boolean,
  roleId: string,
  shortDescription: React.Node,
  descriptionOfWhatRoleCanDo: React.Node,
  descriptionOfWhatRoleCannotDo: React.Node,
  someThingsThisRoleCanDo: Array<React.Node>,
  someThingsThisRoleCannotDo: Array<React.Node>,
|};

type RoleDescriptionsContainerProps = {|
  roleDescriptions: Array<RoleDescriptionProps>,
|};

const messages = defineMessages({
  betaBadge: {
    id: 'docsApp.components.contrib.roleDescription.betaBadge',
    description: 'The text of a badge indicating this role is in beta.',
    defaultMessage: 'Beta',
  },
  someThingsThisRoleCanDo: {
    id: 'docsApp.components.contrib.roleDescription.someThingsThisRoleCanDo',
    description:
      'The text above a bulleted list that describes what the role can do.',
    defaultMessage: 'Some of what this role can do:',
  },
  someThingsThisRoleCannotDo: {
    id: 'docsApp.components.contrib.roleDescription.someThingsThisRoleCannotDo',
    description: "The text above a bulleted list of things the role can't do.",
    defaultMessage: "Some of what this role can't do:",
  },
  ssoRoleId: {
    id: 'docsApp.components.contrib.roleDescription.ssoRoleId',
    description: 'Text explaining that the following value is the sso role id.',
    defaultMessage: 'SSO Role ID:',
  },
});

const renderExpandedDetails = (
  someThingsThisRoleCanDo: Array<React.Node>,
  someThingsThisRoleCannotDo: Array<React.Node>,
) => {
  return (
    <Group spacing={12} padding={{top: 20}}>
      <Group direction="horizontal" spacing={8} alignItems="baseline">
        <Icon icon="check" size={12} color="gray400" />
        <Text weight="medium">
          <FormattedMessage {...messages.someThingsThisRoleCanDo} />
        </Text>
      </Group>
      <Box padding={{horizontal: 32}}>
        <ul type="bulleted" size="small">
          {someThingsThisRoleCanDo.map((thing) => (
            <li>
              <Text lineHeight={24} color="gray700">
                {thing}
              </Text>
            </li>
          ))}
        </ul>
      </Box>

      <Group direction="horizontal" spacing={8} alignItems="baseline">
        <Icon icon="cancel" size={12} color="gray400" />
        <Text weight="medium">
          <FormattedMessage {...messages.someThingsThisRoleCannotDo} />
        </Text>
      </Group>
      <Box padding={{horizontal: 32}}>
        <ul type="bulleted" size="small">
          {someThingsThisRoleCannotDo.map((thing) => (
            <li>
              <Text lineHeight={24} color="gray700">
                {thing}
              </Text>
            </li>
          ))}
        </ul>
      </Box>
    </Group>
  );
};

type CopyButtonProps = {|
  textToCopy: string,
|};

function CopyButton({textToCopy}: CopyButtonProps) {
  const [isShowingCheck, setIsShowingCheck] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy);
    setIsShowingCheck(true);

    setTimeout(() => {
      setIsShowingCheck(false);
    }, 750);
  };

  return (
    <Box
      flex={{direction: 'row'}}
      onClick={handleCopy}
      divider={{side: 'all', color: 'light', width: 1}}
      padding={4}
      alignItems="center"
      style={{borderRadius: '6px'}}
      background="gray50"
    >
      <Group direction="horizontal" alignItems="center" spacing={4}>
        <Text typeface="mono" color="gray500" size={12}>
          {textToCopy}
        </Text>
        <Icon
          icon={isShowingCheck ? 'check' : 'clipboard'}
          size={12}
          color="gray200"
        />
      </Group>
    </Box>
  );
}

export const RoleDescriptionsContainer = ({
  roleDescriptions,
}: RoleDescriptionsContainerProps) => {
  const descriptionsWithDividers = roleDescriptions.map(
    (description, index) => {
      const divider = {side: 'horizontal', width: 1, color: 'light'};
      let borderRadius = '0px';
      if (roleDescriptions.length === 1) {
        divider.side = 'all';
        borderRadius = '5px 5px 5px 5px';
      } else if (index === roleDescriptions.length - 1) {
        divider.side = 'all';
        borderRadius = '0px 0px 5px 5px';
      } else if (index === 0) {
        divider.side = 'all';
        borderRadius = '5px 5px 0px 0px';
      }

      return (
        <Box
          style={{borderRadius}}
          background={index % 2 === 0 ? 'white' : 'offset'}
          divider={divider}
        >
          <RoleDescription {...description} />
          {index < roleDescriptions.length - 2 && (
            <Box
              style={{height: '1px'}}
              divider={{side: 'all', width: 1, color: 'light'}}
            />
          )}
        </Box>
      );
    },
  );

  return (
    <Box
      divider={{side: 'all', width: 1, color: 'light'}}
      style={{borderRadius: '5px'}}
      margin={{vertical: 8}}
    >
      {descriptionsWithDividers}
    </Box>
  );
};

const RoleDescription = ({
  roleName,
  roleId,
  isBeta,
  shortDescription,
  descriptionOfWhatRoleCanDo,
  descriptionOfWhatRoleCannotDo,
  someThingsThisRoleCanDo,
  someThingsThisRoleCannotDo,
}: RoleDescriptionProps) => {
  return (
    <Box flex={{direction: 'row'}} padding={16}>
      <Group flexBasis="25%" spacing={8}>
        <Heading level={5} id={roleId} anchored>
          {roleName}
        </Heading>

        {isBeta && (
          <Badge
            label={<FormattedMessage {...messages.betaBadge} />}
            color="blue"
            icon="beta"
            iconPosition="left"
          />
        )}
      </Group>
      <Group flexBasis="75%" spacing={8}>
        <Group direction="horizontal" alignItems="center" spacing={8}>
          <Icon icon="certificate" color="gray300" size={10} />
          <Text weight="medium" color="gray400" size={12}>
            {shortDescription}
          </Text>
        </Group>

        <Group direction="horizontal" spacing={8} alignItems="baseline">
          <Icon icon="checkmark" color="green" size={12} />
          <Text lineHeight={20} size={14}>
            {descriptionOfWhatRoleCanDo}
          </Text>
        </Group>
        <Group direction="horizontal" spacing={8} alignItems="baseline">
          <Icon icon="cross" color="red" size={12} />
          <Text lineHeight={20} size={14}>
            {descriptionOfWhatRoleCannotDo}
          </Text>
        </Group>

        <Box padding={{left: 20, top: 8}}>
          <ExpandableDetailsButton>
            {renderExpandedDetails(
              someThingsThisRoleCanDo,
              someThingsThisRoleCannotDo,
            )}

            <Box
              divider={{side: 'top', width: 1, color: 'light'}}
              padding={{top: 24}}
              margin={{vertical: 12}}
            >
              <Group alignItems="center" direction="horizontal" spacing={4}>
                <Text weight="medium">
                  <FormattedMessage {...messages.ssoRoleId} />
                </Text>
                <CopyButton textToCopy={roleId} />
              </Group>
            </Box>
          </ExpandableDetailsButton>
        </Box>
      </Group>
    </Box>
  );
};
